import { loadWins } from '@/features/game/persistence';
import { getTodaysRiddle } from '@/features/game/logic';

// Extend ServiceWorkerRegistration to include Background Sync APIs
declare global {
  interface ServiceWorkerRegistration {
    sync?: {
      register(tag: string): Promise<void>;
    };
    periodicSync?: {
      register(tag: string, options: { minInterval: number }): Promise<void>;
    };
  }
}

export interface NotificationSettings {
  enabled: boolean;
  morningTime: string; // Format: "HH:MM"
  afternoonTime: string;
  eveningTime: string;
}

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: false,
  morningTime: '09:00',
  afternoonTime: '14:00',
  eveningTime: '18:00',
};

const STORAGE_KEY = 'riddle-v2:notification-settings';
const TEST_NOTIFICATIONS_KEY = 'riddle-v2:test-notifications-active';

export const saveNotificationSettings = (settings: NotificationSettings): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save notification settings:', error);
  }
};

export const loadNotificationSettings = (): NotificationSettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Failed to load notification settings:', error);
  }
  return DEFAULT_SETTINGS;
};

export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  return await Notification.requestPermission();
};

export const isRiddleSolvedToday = (): boolean => {
  try {
    const wins = loadWins();
    const todaysRiddle = getTodaysRiddle();
    return wins.includes(todaysRiddle.answer);
  } catch (error) {
    console.error('Failed to check if riddle is solved:', error);
    return false;
  }
};

export const scheduleDailyNotifications = async (): Promise<void> => {
  if (!('serviceWorker' in navigator) || !('Notification' in window)) {
    console.log('Service worker or notifications not supported');
    return;
  }

  const settings = loadNotificationSettings();
  if (!settings.enabled || Notification.permission !== 'granted') {
    console.log('Notifications not enabled or permission not granted');
    return;
  }

  try {
    // Register service worker
    const registration = await navigator.serviceWorker.register('/arcade/games/riddl/sw-custom.js', {
      scope: '/arcade/games/riddl/'
    });
    await navigator.serviceWorker.ready;

    // Clear any existing notifications
    await clearAllNotifications();

    // Register for background sync if supported
    if ('sync' in window.ServiceWorkerRegistration.prototype && registration.sync) {
      try {
        await registration.sync.register('notification-sync');
        console.log('Background sync registered for notifications');
      } catch (error) {
        console.log('Background sync not supported:', error);
      }
    }

    // Register for periodic background sync if supported
    if ('periodicSync' in window.ServiceWorkerRegistration.prototype && registration.periodicSync) {
      try {
        await registration.periodicSync.register('notification-check', {
          minInterval: 24 * 60 * 60 * 1000 // 24 hours
        });
        console.log('Periodic background sync registered for notifications');
      } catch (error) {
        console.log('Periodic sync not supported:', error);
      }
    }

    // Send notification schedule to service worker
    const message = {
      type: 'SCHEDULE_NOTIFICATIONS',
      settings: settings
    };

    if (registration.active) {
      registration.active.postMessage(message);
    } else if (registration.waiting) {
      registration.waiting.postMessage(message);
    } else if (registration.installing) {
      registration.installing.postMessage(message);
    }

    console.log('Daily notifications scheduled successfully with background sync support');
  } catch (error) {
    console.error('Failed to schedule daily notifications:', error);
  }
};

// Helper function to check if riddle is solved (used by service worker)
export const checkRiddleSolved = (): boolean => {
  return isRiddleSolvedToday();
};

export const clearAllNotifications = async (): Promise<void> => {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const notifications = await registration.getNotifications();
        notifications.forEach(notification => {
          if (notification.tag.startsWith('riddle-')) {
            notification.close();
          }
        });
      }
    }
  } catch (error) {
    console.error('Failed to clear notifications:', error);
  }
};

export const checkNotificationSupport = (): boolean => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

// Test notification state persistence
export const saveTestNotificationState = (isActive: boolean): void => {
  try {
    localStorage.setItem(TEST_NOTIFICATIONS_KEY, JSON.stringify(isActive));
  } catch (error) {
    console.error('Failed to save test notification state:', error);
  }
};

export const loadTestNotificationState = (): boolean => {
  try {
    const stored = localStorage.getItem(TEST_NOTIFICATIONS_KEY);
    return stored ? JSON.parse(stored) : false;
  } catch (error) {
    console.error('Failed to load test notification state:', error);
    return false;
  }
};

// Test notification function for debugging
export const testNotification = async (): Promise<void> => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return;
  }

  if (Notification.permission !== 'granted') {
    console.log('Notification permission not granted');
    return;
  }

  try {
    // Use direct browser notification API
    const notification = new Notification('Test Notification 🧪', {
      body: 'This is a test notification to verify the system is working.',
      icon: '/arcade/games/riddl/icons/riddl_icon.png',
      tag: 'test-notification',
      requireInteraction: true,
      data: {
        url: '/arcade/games/riddl/',
      },
    });

    // Add click handler
    notification.onclick = () => {
      console.log('Test notification clicked');
      notification.close();
      window.focus();
    };

    console.log('Test notification sent successfully');
  } catch (error) {
    console.error('Failed to send test notification:', error);
  }
};

// Function to manually trigger a notification (for testing)
export const triggerNotification = async (type: 'morning' | 'afternoon' | 'evening'): Promise<void> => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return;
  }

  if (Notification.permission !== 'granted') {
    console.log('Notification permission not granted');
    return;
  }

  try {
    const messages = {
      morning: {
        title: 'Good Morning! ☀️',
        body: 'A fresh riddle is waiting for you. Start your day with a challenge!'
      },
      afternoon: {
        title: 'Afternoon Break! ☕',
        body: 'Take a break and solve today\'s riddle. You\'ve got this!'
      },
      evening: {
        title: 'Evening Challenge! 🌙',
        body: 'Don\'t forget today\'s riddle! Can you solve it before bedtime?'
      }
    };

    const message = messages[type];
    const today = new Date().toDateString();

    // Use direct browser notification API (same as direct test)
    const notification = new Notification(message.title, {
      body: message.body,
      icon: '/arcade/games/riddl/icons/riddl_icon.png',
      tag: `riddle-${type}-${today}-test`,
      requireInteraction: true,
      data: {
        url: '/arcade/games/riddl/',
      },
    });

    // Add click handler
    notification.onclick = () => {
      console.log(`${type} notification clicked`);
      notification.close();
      window.focus();
    };
    
    console.log(`${type} notification triggered successfully`);
  } catch (error) {
    console.error(`Failed to trigger ${type} notification:`, error);
  }
};

// Test notification system - sends notification every 10 minutes when app is not active
export const startTestNotifications = async (): Promise<void> => {
  if (!('serviceWorker' in navigator) || !('Notification' in window)) {
    console.log('Service worker or notifications not supported');
    return;
  }

  if (Notification.permission !== 'granted') {
    console.log('Notification permission not granted');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Send message to service worker to start test notifications
    const message = {
      type: 'START_TEST_NOTIFICATIONS',
      interval: 10 // 10 minutes
    };

    if (registration.active) {
      registration.active.postMessage(message);
    } else if (registration.waiting) {
      registration.waiting.postMessage(message);
    } else if (registration.installing) {
      registration.installing.postMessage(message);
    }

    // Save state to localStorage
    saveTestNotificationState(true);

    console.log('Test notifications started - will send every 10 minutes when app is not active');
  } catch (error) {
    console.error('Failed to start test notifications:', error);
  }
};

// Stop test notifications
export const stopTestNotifications = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service worker not supported');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Send message to service worker to stop test notifications
    const message = {
      type: 'STOP_TEST_NOTIFICATIONS'
    };

    if (registration.active) {
      registration.active.postMessage(message);
    } else if (registration.waiting) {
      registration.waiting.postMessage(message);
    } else if (registration.installing) {
      registration.installing.postMessage(message);
    }

    // Save state to localStorage
    saveTestNotificationState(false);

    console.log('Test notifications stopped');
  } catch (error) {
    console.error('Failed to stop test notifications:', error);
  }
};

// Trigger background sync manually (for testing)
export const triggerBackgroundSync = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service worker not supported');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    if ('sync' in window.ServiceWorkerRegistration.prototype && registration.sync) {
      await registration.sync.register('notification-sync');
      console.log('Background sync triggered manually');
    } else {
      console.log('Background sync not supported');
    }
  } catch (error) {
    console.error('Failed to trigger background sync:', error);
  }
};
